import { FormControl, FormHelperText, FormLabel } from '@apollo/orbit';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { URLField } from 'src/components/urlField/URLField';
import { isValidUrl } from 'src/lib/isValidUrl';

type ValidationState =
  | null // Empty input
  | { type: 'invalid'; message: string }
  | { type: 'unreachable'; message: string }
  | { type: 'reachable'; message: string };

interface EndpointInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * Unified endpoint input component with URL format validation and reachability testing.
 * Shows three clear states: invalid, unreachable, or reachable.
 */
export const EndpointInput = ({
  value,
  onChange,
  placeholder = 'https://example.com',
  label = 'Endpoint URL',
  isRequired = false,
  disabled = false,
  onValidationChange,
}: EndpointInputProps) => {
  const [validationState, setValidationState] = useState<ValidationState>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced validation function
  const debouncedValidate = useCallback((url: string) => {
    // Clear previous timeout and abort previous request
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const trimmedUrl = url.trim();

    // Empty input
    if (!trimmedUrl) {
      setValidationState(null);
      return;
    }

    // Check URL format first
    if (!isValidUrl(trimmedUrl)) {
      setValidationState({
        type: 'invalid',
        message: 'Please enter a valid URL, like https://example.com',
      });
      return;
    }

    // URL format is valid, test reachability after debounce
    timeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await fetch(trimmedUrl, {
          method: 'HEAD',
          mode: 'no-cors',
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setValidationState({
            type: 'reachable',
            message: 'The URL is valid and reachable.',
          });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setValidationState({
            type: 'unreachable',
            message: "This URL can't be reached. Please check and try again.",
          });
        }
      }
    }, 500); // 500ms debounce
  }, []);

  // Validate when value changes
  useEffect(() => {
    debouncedValidate(value);

    // Notify parent of basic URL format validation
    const trimmedValue = value.trim();
    const isValid = !!trimmedValue && isValidUrl(trimmedValue);
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [value, debouncedValidate, onValidationChange]);

  const getStatusDisplay = () => {
    if (!validationState) {
      return null; // No status for empty input
    }

    switch (validationState.type) {
      case 'invalid':
        return (
          <FormHelperText iconType="warning">
            {validationState.message}
          </FormHelperText>
        );
      case 'unreachable':
        return (
          <FormHelperText iconType="warning">
            {validationState.message}
          </FormHelperText>
        );
      case 'reachable':
        return (
          <FormHelperText iconType="success">
            {validationState.message}
          </FormHelperText>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <FormControl isRequired={isRequired}>
        <FormLabel>{label}</FormLabel>
        <URLField
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          isSocket={false}
        />
        {getStatusDisplay()}
      </FormControl>
    </div>
  );
};
